


































































import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import {
    MapViewportData,
    MapObject,
    ChannelGroup,
    Point,
    Channel,
    SelectedChannel,
    IncomingHandle,
    OutgoingHandle,
    FakeMapObject,
} from '@/core/components/networkViewer/types';
import {
    CONTRACTS,
    WindowsServiceContract,
    FetchServiceContract,
} from '@/core/contracts';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import {
    LogisticsNetwork,
    RelocationsType,
    RelocationsDirection,
    MapMarker,
} from '@/dto/';
import { ContextsState, ProductsState } from '@/store';
import { encodeParams } from '@/core/helpers/utils';
import _ from 'lodash';
import {
    SVGTemplate,
    MapTemplate,
    ControlsTemplate,
    MercatorProjection,
} from '@/core/components/networkViewer';
import { latLng, LatLng } from 'leaflet';
import Hammer from 'hammerjs';
import { RelocationsPageArgs } from '../relocations';
import { StocksPageArgs } from '../stocks';

@Component({
    components: {
        SVGTemplate,
        MapTemplate,
        ControlsTemplate,
    },
})
export default class LogisticsNetworkMap extends Vue {
    public visibleVehicle: boolean = true;
    @Prop({ type: Boolean, required: false }) public visibleChannel!: boolean;
    public mapObjects: MapObject[] | null = [];
    public channels: ChannelGroup[] | null = [];
    public center: LatLng = latLng(0, 0);
    public selectionChanged: boolean = false;
    public vehicleMarkers: MapMarker[] = [];

    public selectedChannel?: SelectedChannel | null = null;
    public incomingHandle?: IncomingHandle | null = {
        x: 0,
        y: 0,
        draggable: true,
        dragged: false,
    };
    public outgoingHandle?: OutgoingHandle | null = {
        x: 0,
        y: 0,
        draggable: true,
        dragged: false,
    };
    public fakeMapObject?: FakeMapObject | null = null;
    public hammer: any;
    public backgroundHammer: any;

    private SVG: SVGTemplate | null = null;
    private controls: ControlsTemplate | null = null;
    private element: HTMLElement | null = null;
    private root: SVGElement | null = null;
    private viewport: SVGGElement | null = null;
    private map: MapTemplate | null = null;
    private windowHeight: number = 0;
    private windowWidth: number = 0;
    private zoomSnap: number = 0.2;

    private leadGroupSelected: Map<string, MapObject> = new Map();

    private network: LogisticsNetwork = {
        visible: true,
        zoomDiff: 0,
        panDelta: null,
        preferences: {
            minZoom: 3,
            maxZoom: 15,
            defaultMapCenter: latLng(53, 50),
            defaultZoom: 3,
            mapObject: {
                minDisplayCapacityRadius: 18,
                maxDisplayCapacityRadius: 36,
                minDisplayFlowRadius: 8,
                maxDisplayFlowRadius: 32,
            },
            channel: {
                minDisplayCapacityWidth: 12,
                maxDisplayCapacityWidth: 32,
                minDisplayFlowWidth: 4,
                maxDisplayFlowWidth: 32,
            },
            selection: {
                outlineWidth: 8,
                deleteHandleIndent: 24,
                connectHandleIndent: 48,
                handleRadius: 24,
                minChannelSelectionWidth: 28,
                maxChannelSelectionWidth: 32,
            },
        },
        state: {
            screen: { x: 0, y: 0 }, // screen mouse coordinates
            layer: { x: 0, y: 0 }, // layer mouse coordinates
            offset: { x: 0, y: 0 }, // layer offset
            layerCenter: { x: null, y: null }, // screen center in layer's coordinates with scale
            worldCenter: { x: null, y: null }, // screen center in layer's coordinates without scale
            mapCenter: null, // screen center in lat,lng
            zoom: 3,
            scale: Math.pow(2, 3),
            designMode: false,
            loadDataTimeout: null,
            selectedMapObject: null,
            selectedChannel: null,
            mapIsMoving: false,
            loadDataRequestId: 0,
            _resetViewport: true,
            width: 0,
            height: 0,
            general: {
                maxFlow: 0,
            },
            mapObjects: {
                maxCapacity: null,
                maxFlow: 0,
            },
            channels: {
                maxCapacity: null,
                maxFlow: 0,
            },
        },
        window: {
            width: this.element?.clientWidth ?? 0,
            height: this.element?.clientHeight ?? 0,
        },
    };
    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;

    private settings = {
        showNetworkLabels: true,
        showPostponedLabels: true,
    };

    private model = {
        showAdditionalLabel: false,
        showChannelLimitText: true,
        designMode: false,
    };

    // public: show
    public show() {
        this.clear();
        this.panTo(this.network.preferences.defaultMapCenter);
        this.setZoom(this.network.preferences.defaultZoom);
        this.loadData();
    }

    // create empty network
    public createEmptyData() {
        return { sites: [], channels: [], vehicles: [], viewport: null };
    }

    // public: clear
    public clear() {
        this.selectMapObject(null);
        this.setData(this.createEmptyData());
    }

    public onmousemove(event: any) {
        this.network.state.screen.x = event.clientX;
        this.network.state.screen.y = event.clientY;
        this.updateLayerPosition();
    }

    public onmousewheel(event: any) {
        const target = event?.target;
        const targetClass = target!.className!.toString();
        if (targetClass.indexOf('noZoom') === -1) {
            const delta = event.wheelDelta ? event.wheelDelta : event.detail;
            const sign = delta >= 0 ? this.zoomSnap : -this.zoomSnap;
            let newzoom = (this.network.state.zoom ?? 0) + sign;
            newzoom = Math.min(newzoom, this.network.preferences.maxZoom);
            newzoom = Math.max(newzoom, this.network.preferences.minZoom);

            if (newzoom !== this.network.state.zoom) {
                this.setZoom(newzoom, this.network.state.layer);
                this.loadData();
            }
        }
    }

    public resize() {
        const width = this.element!.clientWidth;
        const height = this.element!.clientHeight;
        const dx = (width - this.network.state.width) / 2;
        const dy = (height - this.network.state.height) / 2;
        this.network.window.width = width;
        this.network.window.height = height;
        this.pan(dx, dy, true);

        this.setWindowSize();
        this.updateRect();

        if (this.network.visible) {
            this.map?.map?.mapObject.invalidateSize();
            this.loadData();
        }
    }

    public toggleVehicle(visible: boolean) {
        this.visibleVehicle = visible;
        this.refresh();
    }

    private mounted() {
        const networkRef = 'network-control';
        const mapRef = 'MAPRef';
        const SVGRef = 'SVG';
        const controlRef = 'ControlRef';
        this.center = latLng(0, 0);

        this.element = this.$refs[networkRef] as HTMLElement;
        this.map = this.$refs[mapRef] as MapTemplate;

        this.SVG = this.$refs[SVGRef] as SVGTemplate;
        this.controls = this.$refs[controlRef] as ControlsTemplate;
        this.root = this.SVG.root as SVGElement;
        this.viewport = this.SVG.viewport as SVGGElement;

        this.hammer = new Hammer(this.root);

        this.network.window.height = this.element.clientHeight;
        this.network.window.width = this.element.clientWidth;

        this.setWindowSize();
        this.setupPan();
        this.setupZoom();
        this.setupControls();
        this.refresh();
    }

    private setVisibility(visible: boolean) {
        if (visible) {
            $('#map').css('display', '');
            $('#network').css('display', '');
        } else {
            $('#map').css('display', 'none');
            $('#network').css('display', 'none');
        }

        const oldVisible = this.network.visible;
        this.network.visible = visible;
        if (oldVisible !== this.network.visible) {
            this.refresh(true);
        }
    }

    private setWindowSize() {
        this.windowHeight = (window.outerHeight + window.innerHeight) / 2;
        this.windowWidth = (window.outerWidth + window.innerWidth) / 2;
    }

    private setupZoom() {
        if (this.element !== null) {
            this.element.addEventListener('mousemove', this.onmousemove, false);
        }
        if (this.root !== null) {
            this.root.addEventListener('mousewheel', this.onmousewheel, false);
        }
        if (this.controls && this.controls.root) {
            this.controls.root.addEventListener(
                'mousewheel',
                this.onmousewheel,
                false
            );
        }

        this.hammer.get('pinch').set({ enable: true });

        this.hammer.on('pinchstart', this.onPinchStart);

        this.hammer.on('pinch', this.onPinch);

        this.hammer.on('pinchend', this.onPinchEnd);
    }

    //#region Hammer Pinch
    private onPinchStart(event: any) {
        this.network.zoomDiff = 0;
    }

    private onPinch(event: any) {
        const diff = Math.log2(event.scale);
        const localDiff = Number(String(diff - this.network.zoomDiff!));
        if (Math.abs(localDiff) >= this.zoomSnap) {
            const newzoom = this.network.state.zoom! + localDiff;
            const point = {
                x:
                    (event.pointers[0].clientX + event.pointers[1].clientX) /
                        2 -
                    this.network.state.offset.x!,
                y:
                    (event.pointers[0].clientY + event.pointers[1].clientY) /
                        2 -
                    this.network.state.offset.y!,
            };
            this.setZoom(newzoom, point);
            this.network.zoomDiff = diff;
            this.loadData();
        }
    }

    private onPinchEnd(event: any) {
        this.network.zoomDiff = null;
    }
    //#endregion

    private setupPan() {
        // set pan
        this.hammer.get('pan').set({
            direction: Hammer.DIRECTION_ALL,
            threshold: 10,
            pointers: 1,
            preventDefault: true,
        });

        this.hammer.on('panstart', this.onPanStart);

        this.hammer.on('pan', this.onPan);

        this.hammer.on('panend', this.onPanEnd);

        this.updateRect();
    }

    //#region Hammer Pan
    private onPanStart(event: any) {
        if (event.target === this.hammer.element) {
            this.network.panDelta = {
                x: 0,
                y: 0,
            };
            this.network.window.width = this.element!.clientWidth;
            this.network.window.height = this.element!.clientHeight;

            this.network.state.mapIsMoving = true;
        }
    }

    private onPan(event: any) {
        const dx = event.deltaX;
        const dy = event.deltaY;

        if (this.network.panDelta === null) {
            return;
        }
        this.pan(dx - this.network.panDelta.x, dy - this.network.panDelta.y);
        this.network.panDelta = {
            x: dx,
            y: dy,
        };
    }

    private onPanEnd(event: any) {
        if (this.network.panDelta === null) {
            return;
        }
        this.network.panDelta = null;
        this.network.state.mapIsMoving = false;
        this.loadData();
    }
    //#endregion

    private setupControls() {
        this.hammer.on('tap', this.onTap);
    }

    //#region Hammer Tap
    private onTap(event: any) {
        if (event.target === this.hammer.element) {
            const point = {
                x: event.pointers[0].clientX - this.network.state.offset.x!,
                y: event.pointers[0].clientY - this.network.state.offset.y!,
            };
            this.selectMapObject(null);
            this.showFakeMapObject(point);
            // $(@).trigger 'mapClicked'
        }
    }

    private onBackGroundTap(event: any) {
        if (event.target === this.backgroundHammer.element) {
            // $(@).trigger 'mapClicked'
        }
    }
    //#endregion

    private panTo(latlng: LatLng) {
        const point = MercatorProjection.prototype.fromLatLngToPoint(latlng);
        this.panToPoint(point);
    }

    private panToPoint(point: any) {
        point.x *= this.network.state.scale;
        point.y *= this.network.state.scale;
        const dx =
            point.x +
            this.network.state.offset.x -
            (this.element?.clientWidth ?? 0) / 2;
        const dy =
            point.y +
            this.network.state.offset.y -
            (this.element?.clientHeight ?? 0) / 2;
        this.pan(-dx, -dy);
    }

    private pan(dx: any, dy: any, resize: boolean = false) {
        const xForm = this.SVG?.viewport?.transform.baseVal.getItem(0);
        if (xForm !== undefined) {
            const width = this.element?.clientWidth ?? 0;
            const height = this.element?.clientHeight ?? 0;

            const offsetX = (width - this.network.state.width) / 2;
            const offsetY = (height - this.network.state.height) / 2;

            const x = xForm.matrix.e;
            const y = xForm.matrix.f;
            const newx = x + dx;
            const newy = y + dy;

            this.SVG?.viewport?.transform.baseVal
                .getItem(0)
                .setTranslate(newx, newy);

            this.network.state.offset.x = newx;
            this.network.state.offset.y = newy;
            this.updateLayerPosition();

            if (offsetX !== 0 && !resize) {
                this.network.state.offset.x =
                    (this.network.state.offset.x ?? 0) + offsetX;
            }

            if (offsetY !== 0 && !resize) {
                this.network.state.offset.y =
                    (this.network.state.offset.y ?? 0) + offsetY;
            }

            this.network.state.layerCenter = {
                x: width / 2 - (this.network.state.offset.x ?? 0),
                y: height / 2 - (this.network.state.offset.y ?? 0),
            };

            this.network.state.worldCenter = {
                x:
                    (this.network.state.layerCenter.x ?? 0) /
                    this.network.state.scale,
                y:
                    (this.network.state.layerCenter.y ?? 0) /
                    this.network.state.scale,
            };

            this.network.state.mapCenter = MercatorProjection.prototype.fromPointToLatLng(
                this.network.state.worldCenter
            );
            this.map?.panTo(this.network.state.mapCenter, {
                animate: false,
                noMoveStart: true,
            });
        }
    }

    private setZoom(newzoom: any, point: any = null) {
        this.network.window.height = this.element!.clientHeight;
        this.network.window.width = this.element!.clientWidth;
        newzoom = Math.min(newzoom, this.network.preferences.maxZoom);
        newzoom = Math.max(newzoom, this.network.preferences.minZoom);
        this.map?.map?.setZoom(newzoom);
        point = point || this.network.state.layerCenter;
        const oldzoom = this.network.state.zoom ?? 0;

        const oldscale = Math.pow(2, oldzoom);
        const scale = Math.pow(2, newzoom);
        this.network.state.zoom = newzoom;
        this.network.state.scale = scale;
        this.mapObjects?.forEach(mapObject => {
            this.updatePosition(
                mapObject,
                mapObject.lat,
                mapObject.lng,
                this.network.state.scale
            );
        });

        const newpoint = {
            x: (point.x / oldscale) * scale,
            y: (point.y / oldscale) * scale,
        };

        this.pan(point.x - newpoint.x, point.y - newpoint.y);
        if (this.network.state.selectedMapObject !== null) {
            this.zoomMapObjectSelectionControl(oldscale, scale);
        }
    }

    private zoomMapObjectSelectionControl(oldscale: any, newscale: any) {
        if (this.outgoingHandle !== undefined && this.outgoingHandle !== null) {
            if (this.outgoingHandle.dragged) {
                this.outgoingHandle.x =
                    (this.outgoingHandle.x / oldscale) * newscale;
                this.outgoingHandle.y =
                    (this.outgoingHandle.y / oldscale) * newscale;
            }
        }
        if (this.incomingHandle !== undefined && this.incomingHandle !== null) {
            if (this.incomingHandle.dragged) {
                this.incomingHandle.x =
                    (this.incomingHandle.x / oldscale) * newscale;
                this.incomingHandle.y =
                    (this.incomingHandle.y / oldscale) * newscale;
            }
        }
    }

    // convert WSG position to projection point
    private updatePosition(item: any, lat: number, lng: number, scale: number) {
        const point = MercatorProjection.prototype.fromLatLng({ lat, lng });
        if (isNaN(point.x) || isNaN(point.y)) {
            throw new Error('Invalid projection coordinates!');
        }
        item.x = Math.abs(scale * point.x);
        item.y = Math.abs(scale * point.y);
    }

    private updateLayerPosition() {
        this.network.state.layer.x =
            (this.network.state.screen.x ?? 0) -
            (this.network.state.offset.x ?? 0);
        this.network.state.layer.y =
            (this.network.state.screen.y ?? 0) -
            (this.network.state.offset.y ?? 0);
    }

    // bind new data
    private setData(data: MapViewportData) {
        // store current selection to reselect after binding
        const mapObjectSelection: any = {
            id: this.network.state.selectedMapObject?.id,
        };

        const channelSelection: any = {
            id: this.selectedChannel?.channel.id,
        };

        // clear datasource
        if (this.channels !== null) {
            this.channels = null;
        }

        if (this.mapObjects !== null) {
            this.mapObjects = null;
        }

        if (data === null) {
            const emptyData = this.createEmptyData();
            this.mapObjects = emptyData.sites;
            this.channels = emptyData.channels;
            return;
        }

        // preprocess mapObjects
        const mapObjects: MapObject[] = [];
        let mapObjectsMaxFlow: any = 0;
        let mapObjectsMaxCapacity: any = null;

        data.sites!.forEach(site => {
            mapObjects.push(site); // store sites by id for channel binding
            this.prepareMapObject(site);

            // calculate global flow
            if (
                mapObjectsMaxCapacity === null ||
                (site?.capacity ?? 0) > mapObjectsMaxCapacity
            ) {
                mapObjectsMaxCapacity = site.capacity;
            }
            mapObjectsMaxFlow = Math.max(mapObjectsMaxFlow, site?.flow ?? 0);

            // find information about selected mapObject
            if (
                mapObjectSelection.id !== null &&
                mapObjectSelection.mapObject === null
            ) {
                if (site.id === mapObjectSelection.id) {
                    mapObjectSelection.mapObject = site;
                }
            }
        });

        this.network.state.mapObjects.maxCapacity = mapObjectsMaxCapacity;
        this.network.state.mapObjects.maxFlow = mapObjectsMaxFlow;

        // preprocess channels
        let channelsMaxCapacity: number | null = null;
        let channelsMaxFlow = 0;

        if (data.sites !== null && data.sites !== undefined) {
            data.channels!.forEach(channel => {
                const point1 = mapObjects.find(
                    mo =>
                        mo.groupId === channel.point1.siteId ||
                        mo.id === channel.point1.siteId
                );
                const point2 = mapObjects.find(
                    mo =>
                        mo.groupId === channel.point2.siteId ||
                        mo.id === channel.point1.siteId
                );
                if (point1 && point2) {
                    channel.point1.mapObject = point1;
                    channel.point2.mapObject = point2;
                    delete channel.point1.siteId;
                    delete channel.point2.siteId;
                    this.prepareChannelPoint(channel.point1);
                    this.prepareChannelPoint(channel.point2);
                    channelsMaxFlow = Math.max(
                        channelsMaxFlow,
                        channel.point1.flow || 0
                    );
                    channelsMaxFlow = Math.max(
                        channelsMaxFlow,
                        channel.point2.flow || 0
                    );
                    if (
                        channelsMaxCapacity === null ||
                        channel.capacity > channelsMaxCapacity
                    ) {
                        channelsMaxCapacity = channel.capacity;
                    }

                    // find information about selected channel
                    if (
                        channelSelection.id !== null &&
                        channelSelection.channel === null
                    ) {
                        if (
                            channel.point1.current !== null &&
                            channel.point1.current.id === channelSelection.id
                        ) {
                            channelSelection.channel = channel;
                            channelSelection.related = channel.point1.current;
                            channelSelection.point = channel.point1;
                        }

                        if (
                            channel.point2.current !== null &&
                            channel.point2.current.id === channelSelection.id
                        ) {
                            channelSelection.channel = channel;
                            channelSelection.related = channel.point2.current;
                            channelSelection.point = channel.point2;
                        }
                    }
                }
            });
        }

        this.network.state.channels.maxCapacity = channelsMaxCapacity;
        this.network.state.channels.maxFlow = channelsMaxFlow;
        this.network.state.general.maxFlow = Math.max(
            this.network.state.channels.maxFlow,
            this.network.state.mapObjects.maxFlow
        );

        // bind data and reselect mapObject
        this.mapObjects = mapObjects;
        this.channels = data.channels;

        // reselect channel and mapObject
        if (
            channelSelection.channel !== null &&
            channelSelection.channel !== undefined
        ) {
            this.selectChannel(
                channelSelection.channel,
                channelSelection.point,
                channelSelection.related,
                null,
                false
            );
        } else {
            this.selectMapObject(
                mapObjectSelection.mapObject,
                mapObjectSelection.related,
                mapObjectSelection.mapObject === null
            );
        }

        // fit data to the screen
        if (this.network.state._resetViewport && data.viewport !== null) {
            this.resetViewport(data);
            this.network.state._resetViewport = false;
        }
    }

    // update channel point for binding after loading from server
    private prepareChannelPoint(point: Point) {
        this.updatePointRepresentation(point);
        point.expanded = false;
    }

    // update point representation with most valuable channel
    private updatePointRepresentation(point: Point) {
        point.groupId = point.id;
        if (point.current) {
            point.id = point.current.id;
            point.name = point.current.name;
        } else {
            delete point.id;
            delete point.name;
        }
    }

    // create network filter
    private createFilter() {
        const filter = {
            onlyFlow: false,
            mapObject: '',
            productId: ProductsState.current,
        };

        return filter;
    }

    private updateRect() {
        if (this.element !== null) {
            this.network.state.width = this.element.clientWidth;
            this.network.state.height = this.element.clientHeight;
        }
    }

    private loadData() {
        if (!this.network.visible) {
            return;
        }

        // start loading thread after short delay
        if (
            this.network.state.loadDataTimeout !== null &&
            this.network.state.loadDataTimeout !== undefined
        ) {
            clearTimeout(this.network.state.loadDataTimeout);
        }
        this.network.state.loadDataTimeout = setTimeout(
            this.loadDataThread,
            250
        );
    }

    private async loadDataThread() {
        if (!this.network?.visible) {
            return;
        }
        const requestId = this.network.state.loadDataRequestId + 1;
        this.network.state.loadDataRequestId = requestId;
        const contextId = ContextsState.currentContextId;
        if (contextId == null) {
            return;
        }

        const viewportWidth = Math.floor(
            Math.floor(this.element!.clientWidth) * 1.5
        );
        const viewportHeight = Math.floor(
            Math.floor(this.element!.clientHeight) * 1.5
        );

        const filter = this.createFilter();

        const params = {
            zoom: null as any,
            x: null as any,
            y: null as any,
            width: viewportWidth,
            height: viewportHeight,
            selectedMapObject:
                this.network.state.selectedMapObject?.id === undefined
                    ? ''
                    : this.network.state.selectedMapObject?.id,
            selectedChannel:
                this.network.state.selectedChannel?.id === undefined
                    ? ''
                    : this.network.state.selectedChannel?.id,
            filter: JSON.stringify(filter),
            vehicleVisible: this.visibleVehicle,
            vehicleChannel: this.visibleChannel,
        };

        if (!this.network.state._resetViewport) {
            params.zoom = this.network.state.zoom;
            params.x = parseInt(
                String(
                    (this.network.state.layerCenter.x ?? 0) - viewportWidth / 2
                ),
                10
            );
            params.y = parseInt(
                String(
                    (this.network.state.layerCenter.y ?? 0) - viewportHeight / 2
                ),
                10
            );
        }
        const urlParams = params;
        const url = `/contexts/${contextId}/map/viewport?${encodeParams(
            urlParams
        )}`;
        const data = await this._fetchService!.get<MapViewportData>(url);

        if (this.network.state.loadDataRequestId > requestId) {
            return;
        }

        this.setData(data);
    }

    private selectChannel(
        _group: ChannelGroup,
        _point: Point,
        related: Channel,
        event: any,
        trigger: boolean = true
    ) {
        if (this.network.state.mapIsMoving) {
            return;
        }

        this.selectionChanged = false;

        // deselect channel
        if (this.selectedChannel !== null) {
            this.selectedChannel = null;
            this.selectionChanged = true;
        }

        // select new channel
        if (_group !== null) {
            if (_point === null) {
                // calculate selected point from click/tap position
                const x = event.clientX - (this.network.state.offset.x ?? 0);
                const y = event.clientY - (this.network.state.offset.y ?? 0);
                const point1Distance = Math.sqrt(
                    Math.pow(_group.point1.mapObject.x - x, 2) +
                        Math.pow(_group.point1.mapObject.y - y, 2)
                );
                const point2Distance = Math.sqrt(
                    Math.pow(_group.point2.mapObject.x - x, 2) +
                        Math.pow(_group.point2.mapObject.y - y, 2)
                );
                _point =
                    point1Distance <= point2Distance
                        ? _group.point1
                        : _group.point2;
            }

            let _backwardPoint =
                _point === _group.point1 ? _group.point2 : _group.point1;

            if (_point.flow == null) {
                [_point, _backwardPoint] = [_backwardPoint, _point];
            }

            // calculate selected channel
            if (related == null) {
                related = _point.current;
                _point.id = related.id;
                _point.name = related.name;
                _point.expanded = false;
            }

            // store information about selected channel
            this.selectedChannel = {
                group: _group,
                channel: related,
                point: _point,
                backwardPoint: _backwardPoint,
                expanded: false,
            };

            this.selectionChanged = true;

            // select destination mapObject from selected channel
            this.selectMapObject(_point.mapObject, null, true, false, false);

            // if selectionChanged and trigger
            //     $(@).trigger 'selected',
            //         mapObject: {alias: @state.selectedSite?.id, name: @state.selectedSite?.name}
        }
    }

    // select mapObject
    private selectMapObject(
        mapObject: MapObject | null,
        related?: MapObject | null,
        updateSelectionControl: boolean = true,
        trigger: boolean = true,
        deselectChannel: boolean = true
    ) {
        if (
            _.isEqual(this.network.state.selectedMapObject, mapObject) &&
            this.network.state.selectedMapObject?.id === mapObject?.id &&
            related === null
        ) {
            return;
        }

        this.hideFakeMapObject();
        this.selectionChanged = false;

        // deselect mapObject
        if (
            this.network.state.selectedMapObject !== null &&
            this.network.state.selectedMapObject !== undefined
        ) {
            if (this.network.state.selectedMapObject.isGroup) {
                if (
                    related &&
                    mapObject &&
                    mapObject.groupId &&
                    updateSelectionControl
                ) {
                    this.leadGroupSelected.set(mapObject.groupId, related);
                }

                this.updateGroupRepresentation(
                    this.network.state.selectedMapObject
                );
            }

            this.network.state.selectedMapObject.selected = false;

            if (this.network.state.selectedMapObject.expanded) {
                this.network.state.selectedMapObject.expanded = false;
            }

            this.network.state.selectedMapObject.nameEditable = false;
            this.network.state.selectedMapObject = null;
            this.selectionChanged = true;
        }

        // deselect channel
        if (deselectChannel) {
            this.selectedChannel = null;
        }

        // select new mapObject
        if (mapObject) {
            if (mapObject.isGroup) {
                if (related === null) {
                    related = mapObject.current;
                }
                if (related !== undefined && related !== null) {
                    mapObject.id = related.id;
                    mapObject.name = related.name;
                    mapObject.objectType = related.objectType;
                    mapObject.description = related.description;
                }
                mapObject.expanded = false;
            }

            mapObject.selected = true;
            this.network.state.selectedMapObject = mapObject;
            this.selectionChanged = true;
        }

        // if selectionChanged and trigger
        //     $(@).trigger 'selected',
        //         mapObject: {alias: @state.selectedSite?.id, name: @state.selectedSite?.name}

        // update controls
        if (updateSelectionControl) {
            this.updateMapObjectSelectionControl();
        }
    }

    private detailed(item: MapObject) {
        if (item.id) {
            if (item.objectType === 1) {
                this._windowsService!.open('relocations-page', {
                    type: RelocationsType.FIXED,
                    direction: RelocationsDirection.INCOMING,
                    vehicleId: item.id,
                } as RelocationsPageArgs);
            } else {
                this._windowsService!.open('stocks-page', {
                    siteAlias: item.id,
                } as StocksPageArgs);
            }
        }
    }

    private showFakeMapObject(point: any) {
        this.hideFakeMapObject();
        const fakeMapObject = {
            x: point.x,
            y: point.y,
            r: 16,
            ready: false,
        };

        this.fakeMapObject = fakeMapObject;

        // wait for 15 ms to protect from accidental double click
        setTimeout(this.setFakeMapObjectReady, 15);
    }

    private setFakeMapObjectReady() {
        if (this.fakeMapObject !== null && this.fakeMapObject !== undefined) {
            this.fakeMapObject.ready = true;
        }
    }

    // hide fake mapObject
    private hideFakeMapObject() {
        this.fakeMapObject = null;
    }

    private createIndependentMapObject(x: any, y: any) {
        if (
            this.fakeMapObject !== null &&
            this.fakeMapObject !== undefined &&
            !this.fakeMapObject.ready
        ) {
            return;
        }
        this.hideFakeMapObject();
        const position = {
            x: x / this.network.state.scale,
            y: y / this.network.state.scale,
        };

        const mapObject = this.createMapObject(position);
        // TODO Add API reference
        // const result;
        // = api.createSite @getContextId(), mapObject.name, mapObject.lat, mapObject.lng
        // mapObject.id = result.siteId;
        this.selectMapObject(mapObject);
    }

    // create and add mapObject to datasource
    private createMapObject(position: any) {
        const latlng = MercatorProjection.prototype.toLatLng(position);
        const mapObject: MapObject = {
            id: '',
            name: 'User mapObject',
            lat: latlng.lat,
            lng: latlng.lng,
            alias: '',
            data: null,
            x: 0,
            y: 0,
            capacity: 0,
            scheduledFlow: 0,
            infeasibleFlow: 0,
            fixedFlow: 0,
            unusualFlow: 0,
            hasPostponed: false,
            flow: 0,
            additionalLabel: 0,
            fake: false,
            isGroup: false,
            groupId: '',
            selected: false,
            expanded: false,
            nameEditable: false,
            draggable: false,
            current: false,
        };

        this.prepareMapObject(mapObject);
        this.mapObjects?.push(mapObject);
        return mapObject;
    }

    // update mapObject/group for binding after loading from server
    private prepareMapObject(mapObject: MapObject) {
        mapObject.selected = false;
        mapObject.nameEditable = false;
        if (!mapObject.capacity) {
            mapObject.capacity = mapObject.flow;
        }
        if (mapObject.isGroup) {
            mapObject.groupId = mapObject.id;
            const leadMapObject = this.leadGroupSelected.get(mapObject.groupId);
            if (leadMapObject) {
                mapObject.current = leadMapObject;
            }
            this.updateGroupRepresentation(mapObject);
            mapObject.expanded = false;
        } else {
            mapObject.isGroup = false;
        }

        mapObject.draggable = this.model.designMode;
        this.updatePosition(
            mapObject,
            mapObject.lat,
            mapObject.lng,
            this.network.state.scale
        );
    }

    // update group representation with most valuable mapObject
    private updateGroupRepresentation(group: MapObject) {
        group.id = group.current.id;
        group.name = group.current.name;
        group.description = group.current.description;
        group.objectType = group.current.objectType;
        return group;
    }

    private updateMapObjectSelectionControl() {
        this.outgoingHandle = this.incomingHandle = null;
        if (this.network.state.selectedMapObject == null) {
            return;
        }

        this.outgoingHandle = {
            x: 100,
            y: 100,
            draggable: true,
            dragged: false,
        };

        this.incomingHandle = {
            x: -100,
            y: -100,
            draggable: true,
            dragged: false,
        };
    }
    // update mapObject name from ui
    private updateMapObjectName(mapObject: MapObject, name: string) {
        // TODO Add API reference or analog
        // api.updateSiteName @getContextId(), mapObject.id, name
    }

    // public: refresh
    // resetViewport - calculate viewport position to fit network to the user screen
    private refresh(resetViewport: boolean = false) {
        if (resetViewport) {
            this.network.state._resetViewport = true;
        }
        this.loadData();
    }

    private resetViewport(data: any) {
        if (this.viewport !== null) {
            this.panToPoint(data.viewport);
            this.setZoom(data.viewport.zoom);
        }
    }

    private zoomIn() {
        this.setZoom((this.network.state.zoom ?? 0) + this.zoomSnap);
        this.refresh();
    }

    private zoomOut() {
        this.setZoom((this.network.state.zoom ?? 0) - this.zoomSnap);
        this.refresh();
    }

    // calculate zoom and viewport position
    private calculateViewport(mapObjects: any) {
        if (mapObjects.length === 0) {
            return;
        }

        let minX: any = Number.MAX_VALUE;
        let maxX: any = Number.MIN_VALUE;
        let minY: any = Number.MAX_VALUE;
        let maxY: any = Number.MIN_VALUE;

        mapObjects.forEach((mapObjectLocal: any) => {
            const lat = mapObjectLocal.lat;
            const lng = mapObjectLocal.lng;
            const point = MercatorProjection.prototype.fromLatLng({ lat, lng });

            if ((!minX || minX) > point.x) {
                minX = point.x;
            }
            if ((!maxX || maxX) < point.x) {
                maxX = point.x;
            }
            if ((!minY || minY) > point.y) {
                minY = point.y;
            }
            if ((!maxY || maxY) < point.y) {
                maxY = point.y;
            }
        });

        const width = maxX - minX;
        const height = maxY - minY;
        const widthZoom =
            width === 0
                ? 0
                : Math.floor(
                      Math.log(this.network.state.width / width) / Math.LN2
                  );
        const heightZoom =
            height === 0
                ? 0
                : Math.floor(
                      Math.log(this.network.state.height / height) / Math.LN2
                  );
        const _zoom = Math.min(widthZoom, heightZoom);

        return {
            zoom: _zoom,
            point: {
                x: (minX + maxX) / 2,
                y: (minY + maxY) / 2,
            },
        };
    }
}
